/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { PropsWithChildren } from "react";

import * as React from "react";
import styled from "styled-components";
import { webComponentShim } from "components/shared/WebComponentShim";
import { fontFamilies } from "components/shared/Styles";

export const HugeSwitchContainer = styled(webComponentShim("sp-switch"))`
  --mod-switch-control-width: 58px;
  --mod-switch-control-height: 32px;
  --mod-switch-spacing-top-to-control: 0px;
  --mod-switch-spacing-top-to-label: 0px;
  --mod-border-width-200: 3px;
  --mod-switch-background-color: #d3d5ff;
`;

interface EnhanceLabelProps {
  isDimmed?: boolean;
}

export const EnhanceSpeechToggleLabel = styled.label<EnhanceLabelProps>`
  font: normal normal 900 22px/27px ${fontFamilies};
  color: var(--spectrum-global-color-static-indigo-600);
  opacity: ${({ isDimmed }): number => (isDimmed ? 0.5 : 1)};
`;

const OriginalToggleLabel = styled.label<EnhanceLabelProps>`
  font: normal normal 900 22px/27px ${fontFamilies};
  color: var(--spectrum-global-color-static-indigo-600);
  padding-right: 16px;
  opacity: ${({ isDimmed }): number => (isDimmed ? 0.5 : 1)};
`;

export const EnhancedToggle: React.FC<
  PropsWithChildren<{
    checked?: boolean;
    onClick: () => void;
  }>
> = (props) => {
  return (
    <>
      <OriginalToggleLabel isDimmed={props.checked}>
        {I18n.t("enhance_speech.controls.toggle.original")}
      </OriginalToggleLabel>
      <HugeSwitchContainer
        data-testid="enhance-preview-toggle"
        emphasized
        {...props}
      />
      <EnhanceSpeechToggleLabel isDimmed={!props.checked}>
        {I18n.t("enhance_speech.controls.toggle.enhanced")}
      </EnhanceSpeechToggleLabel>
    </>
  );
};

const EnhancedSwitch: React.FC<
  PropsWithChildren<{
    checked?: boolean;
    onClick: () => void;
  }>
> = (props) => {
  return (
    <HugeSwitchContainer
      data-testid="enhance-preview-toggle"
      emphasized
      {...props}
    >
      <EnhanceSpeechToggleLabel>
        {I18n.t("enhance_speech.enhance_speech")}
      </EnhanceSpeechToggleLabel>
    </HugeSwitchContainer>
  );
};

export default EnhancedSwitch;
